
if (!customElements.get('discount-progress-bar')) {
	const CART_UPDATE_EVENT = 'cart:update';

	const RENDER_DEBOUNCE_MS = 100;

	class DiscountProgressBar extends HTMLElement {
		static get observedAttributes() {
			return ['data-cart-total'];
		}

		constructor() {
			super();

			this.boundHandleCartUpdate = null;
			this.boundHandleSectionReload = null;

			this.state = {
				cartTotal: 0,
				currencySymbol: '$',
				goals: [],
				currentGoalIndex: -1,
				previousGoalIndex: -1,
				progressPercentage: 0
			};

			this.elements = {
				progressFill: null,
				progressText: null,
				milestones: []
			};

			this.animationFrameId = null;
			this.updateTimer = null;
		}

		connectedCallback() {
			this.initializeComponent();
			this.setupEventListeners();
			this.updateProgressBar();
		}

		disconnectedCallback() {
			this.cleanup();
		}

		attributeChangedCallback(name, oldValue, newValue) {
			if (name !== 'data-cart-total' || oldValue === newValue) return;
			if (!this.isConnected) return;

			this.scheduleRender();
		}

		scheduleRender() {
			clearTimeout(this.updateTimer);

			this.updateTimer = setTimeout(() => {
				this.initializeComponent();
				this.updateProgressBar();
			}, RENDER_DEBOUNCE_MS);
		}

		initializeComponent() {
			const cartTotalAttr = this.getAttribute('data-cart-total');
			this.state.cartTotal = cartTotalAttr ? parseFloat(cartTotalAttr) : 0;
			this.state.currencySymbol = this.getAttribute('data-currency-symbol') || '$';
			this.elements.progressFill = this.querySelector('.progress-bar-fill');
			this.elements.progressText = this.querySelector('.progress-text');
			this.elements.milestones = Array.from(this.querySelectorAll('.progress-milestone'));


			this.state.goals = this.elements.milestones
				.map((milestone, index) => {
					const goalAmount = parseFloat(milestone.getAttribute('data-goal')) || 0;
					if (goalAmount <= 0) return null;

					return {
						index,
						amount: goalAmount,
						element: milestone,
						title: milestone.querySelector('.milestone-discount')?.textContent || '',
						description: milestone.querySelector('.milestone-label')?.textContent || '',
						messageBefore: this.getGoalMessage(index + 1, 'before'),
						messageReached: this.getGoalMessage(index + 1, 'reached')
					};
				})
				.filter(goal => goal !== null)
				.sort((a, b) => a.amount - b.amount);

			if (this.state.goals.length === 0) {
				console.warn('DiscountProgressBar: No valid goals configured');
			}
		}

		getGoalMessage(goalNumber, type) {
			const script = this.querySelector(`script[data-goal-${goalNumber}-${type}]`);
			return script ? script.textContent.trim() : '';
		}

		setupEventListeners() {
			this.boundHandleCartUpdate = this.handleCartUpdate.bind(this);
			this.boundHandleSectionReload = this.handleSectionReload.bind(this);

			document.addEventListener(CART_UPDATE_EVENT, this.boundHandleCartUpdate);
			document.addEventListener('shopify:section:load', this.boundHandleSectionReload);
		}

		handleCartUpdate(event) {
			const cart = event && event.detail ? event.detail.resource : null;

			if (cart && typeof cart.original_total_price === 'number') {
				this.updateCartTotal(this.extractCartTotal(cart));
				return;
			}

			this.fetchCartAndUpdate();
		}

		extractCartTotal(cart) {
			if (typeof cart.original_total_price === 'number') {
				return cart.original_total_price / 100;
			}
			if (typeof cart.original_total_price === 'string') {
				return parseFloat(cart.original_total_price.replace(/[^0-9.]/g, ''));
			}
			return this.state.cartTotal;
		}

		async fetchCartAndUpdate() {
			try {
				const response = await fetch('/cart.js');
				const cart = await response.json();
				const newTotal = this.extractCartTotal(cart);
				this.updateCartTotal(newTotal);
			} catch (error) {
				console.error('DiscountProgressBar: Error fetching cart', error);
			}
		}

		updateCartTotal(newTotal) {
			if (typeof newTotal !== 'number' || Number.isNaN(newTotal)) return;

			this.state.cartTotal = newTotal;
			this.setAttribute('data-cart-total', newTotal);

			this.scheduleRender();
		}

		updateProgressBar() {
			if (!this.elements.progressFill || this.state.goals.length === 0) return;
			this.calculateProgress();
			this.animateProgressBar();
			this.updateMilestones();
			this.updateMessage();
		}

		calculateProgress() {
			const cartTotal = this.state.cartTotal;
			this.state.previousGoalIndex = this.state.currentGoalIndex;

			let currentGoalIndex = -1;
			let progressPercentage = 0;

			for (let i = 0; i < this.state.goals.length; i++) {
				const goal = this.state.goals[i];

				if (cartTotal >= goal.amount) {
					currentGoalIndex = i;
				} else {
					const previousGoal = i > 0 ? this.state.goals[i - 1] : { amount: 0 };
					const range = goal.amount - previousGoal.amount;
					const progress = cartTotal - previousGoal.amount;
					const goalWidth = 100 / this.state.goals.length;
					progressPercentage = (i * goalWidth) + ((progress / range) * goalWidth);
					progressPercentage = Math.max(0, Math.min(100, progressPercentage));
					break;
				}
			}

			if (currentGoalIndex === this.state.goals.length - 1) {
				progressPercentage = 100;
			}

			this.state.currentGoalIndex = currentGoalIndex;
			this.state.progressPercentage = progressPercentage;
		}

		animateProgressBar() {
			const progressFill = this.elements.progressFill;
			const percentage = this.state.progressPercentage;
			const goalJustReached = this.state.currentGoalIndex > this.state.previousGoalIndex;
			requestAnimationFrame(() => {
				progressFill.style.width = `${percentage}%`;
				progressFill.setAttribute('data-progress', percentage.toFixed(2));

				if (goalJustReached) {
					progressFill.classList.add('goal-reached');
					setTimeout(() => {
						progressFill.classList.remove('goal-reached');
					}, 600);
				}
			});
		}

		updateMilestones() {
			this.state.goals.forEach((goal, index) => {
				const element = goal.element;

				element.classList.remove('active', 'reached', 'next-goal');

				if (index < this.state.currentGoalIndex) {
					element.classList.add('reached');
				} else if (index === this.state.currentGoalIndex) {
					element.classList.add('active', 'reached');
				} else if (index === this.state.currentGoalIndex + 1) {
					element.classList.add('next-goal');
				}
			});
		}

		updateMessage() {
			if (!this.elements.progressText) return;

			const cartTotal = this.state.cartTotal;
			const nextGoalIndex = this.state.currentGoalIndex + 1;

			let message = '';
			if (this.state.currentGoalIndex >= this.state.goals.length - 1 &&
				cartTotal >= this.state.goals[this.state.goals.length - 1].amount) {
				const lastGoal = this.state.goals[this.state.goals.length - 1];
				message = lastGoal.messageReached || 'Congratulations! You\'ve unlocked all rewards!';
			}
			else if (nextGoalIndex < this.state.goals.length) {
				const nextGoal = this.state.goals[nextGoalIndex];
				const amountNeeded = nextGoal.amount - cartTotal;

				if (amountNeeded > 0) {
					message = nextGoal.messageBefore || '';
					const formattedAmount = `<strong>${ this.formatCurrency(amountNeeded) }</strong>`;
					message = message.replace(/\{amount\}/g, formattedAmount);
				} else {
					message = nextGoal.messageReached || '';
				}
			}

			this.updateMessageText(message);
		}

		updateMessageText(newMessage) {
			const textElement = this.elements.progressText;
			const currentMessage = textElement.textContent.trim();
			if (currentMessage === newMessage) return;
			textElement.classList.remove('visible');

			setTimeout(() => {
				textElement.innerHTML = `<span>${newMessage}</span>`;

				requestAnimationFrame(() => {
					textElement.classList.add('visible');
				});
			}, 200);
		}


		formatCurrency(amount) {
			const formatted = amount;
			return `${this.state.currencySymbol}${formatted}`;
		}

		handleSectionReload() {
			this.initializeComponent();
			this.updateProgressBar();
		}

		cleanup() {
			clearTimeout(this.updateTimer);

			if (this.animationFrameId) {
				cancelAnimationFrame(this.animationFrameId);
			}

			// Removal needs the same bound references that were added, otherwise the
			// listeners survive the element and pile up on every re-render.
			if (this.boundHandleCartUpdate) {
				document.removeEventListener(CART_UPDATE_EVENT, this.boundHandleCartUpdate);
			}

			if (this.boundHandleSectionReload) {
				document.removeEventListener('shopify:section:load', this.boundHandleSectionReload);
			}
		}

		setCartTotal(newTotal) {
			this.updateCartTotal(newTotal);
		}

		getState() {
			return { ...this.state };
		}
	}

	customElements.define('discount-progress-bar', DiscountProgressBar);
}
